import React, { useState, useEffect } from 'react';
import HeroCarousel from '../components/home/HeroCarousel';
import BenefitsSection from '../components/home/BenefitsSection';
import ProductGrid from '../components/products/ProductGrid';
// import { ProductProps } from '../components/products/ProductCard';

import { Category, categoryService } from '../services/category.service';
import { SectionResponse } from '../types/section';
import { Product } from '../types/product';
import { productService, ProductItem } from '../services/product.service';
import { cacheService } from '../services/cache.service';

// Define the order of categories to display on home page (matching Header menu)
// Updated order according to new requirements: 1-9 as specified, excluding hop-qua-1-ngan
const HOME_CATEGORY_SLUGS = [
  'hop-trai-cay-cat-san',        // 1. Hộp trái cây cắt sẵn
  'hoa-qua-dam',                 // 2. Hoa quả dầm
  'hop-mix-cat-san-goi-y',       // 3. Hộp mix sẵn gợi ý
  'sua-chua-hy-lap',             // 4. Sữa chua Hy Lạp
  'hop-qua-4-ngan',              // 5. Hộp quà 4 ngăn
  'hop-qua-6-ngan',              // 6. Hộp quà 6 ngăn
  'hop-qua-9-ngan',              // 7. Hộp quà 9 ngăn
  'nuoc-ep-smoothie',            // 8. Nước ép
  'trai-cay-nguyen-qua',         // 9. Trái cây nguyên quả
  // Note: hop-qua-1-ngan is hidden as requested
  'hop-qua-2-tang',              // Additional categories
  'hop-qua-picnic'
];

const Home: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sections, setSections] = useState<SectionResponse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<{
    [key: string]: ProductItem[];
  }>({});

  useEffect(() => {
    // const fetchSections = async () => {
    //   try {
    //     const sectionsData = await sectionService.getSections();
    //     setSections(sectionsData);
    //   } catch (err) {
    //     setError('Đã có lỗi xảy ra khi tải dữ liệu');
    //     console.error('Error fetching sections:', err);
    //   } finally {
    //     setLoading(false);
    //   }
    // };

    // fetchSections();

    const fetchCategories = async () => {
      try {
        // Clear cache to get fresh data with variants
        cacheService.clearAll();
        
        const categoriesData = await categoryService.getPublicCategories();
        // console.log('All categories from API:', categoriesData);
        
        // Filter and sort categories according to HOME_CATEGORY_SLUGS order
        const filteredCategories = HOME_CATEGORY_SLUGS
          .map(slug => {
            const found = categoriesData.find(cat => cat.slug === slug);
            if (!found) {
              console.warn(`Category with slug "${slug}" not found in API response`);
            }
            return found;
          })
          .filter(Boolean) as Category[];
        
        // console.log('Filtered categories for home:', filteredCategories);
        setCategories(filteredCategories);

        // Fetch products for each filtered category
        const productsPromises = filteredCategories.map(async (category) => {
          try {
            const response = await productService.getAllProducts({
              page: 1,
              limit: 12,
              categoryslug: category.slug,
            }, { forceFresh: true });
            // console.log(`Products for ${category.slug}:`, response.products);
            // console.log(`First product type and variants:`, {
            //   type: response.products[0]?.type,
            //   variants: response.products[0]?.variants,
            //   hasType: 'type' in response.products[0],
            //   hasVariants: 'variants' in response.products[0]
            // });
            return { categorySlug: category.slug, products: response.products };
          } catch (err) {
            console.error(
              `Error fetching products for category ${category.slug}:`,
              err
            );
            return { categorySlug: category.slug, products: [] };
          }
        });

        const productsResults = await Promise.all(productsPromises);
        const productsMap = productsResults.reduce(
          (acc, { categorySlug, products }) => {
            acc[categorySlug] = products;
            return acc;
          },
          {} as { [key: string]: ProductItem[] }
        );

        // console.log('Products map:', productsMap);
        setCategoryProducts(productsMap);
      } catch (err) {
        setError('Đã có lỗi xảy ra khi tải dữ liệu');
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const mapToProduct = (products: ProductItem[]): Product[] => {
    return products.map((product) => {
      // console.log('product', product);
      return {
        ...product,
        id: product.id,
        name: product.name,
        description: product?.description,
        images: product.images,
        unit: product?.unit,
        original_price: product.original_price,
        is_on_sale: product.is_on_sale,
        is_trending: product.is_trending,
        status: product?.status,
        type: (product as any).type,
        variants: (product as any).variants,
        sale_price: (product as any).sale_price,
      };
    });
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-12'>
        <div className='bg-red-50 text-red-700 p-4 rounded-md'>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <HeroCarousel />

      {/* Sections from API */}
      {categories.map((category, index) => {
        const categoryProductsList = categoryProducts[category.slug] || [];
        // console.log(`Rendering category ${category.name} with ${categoryProductsList.length} products`);
        
        // Define which categories are hot
        const hotCategories = ['hop-trai-cay-cat-san', 'hop-qua-4-ngan'];
        const isHot = hotCategories.includes(category.slug);
        
        return (
          <ProductGrid
            key={category._id}
            products={mapToProduct(categoryProductsList)}
            title={category.name}
            viewAllLink={`/categories/${category.slug}`}
            bgColor={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
            homeLayout={true}
            showEvenIfEmpty={true} // Add this prop to show categories even without products
            isHot={isHot} // Add hot indicator for specific categories
          />
        );
      })}

      {/* Benefits Section */}
      <BenefitsSection />
    </div>
  );
};

export default Home;
